import { BrandMark } from '@dnx/ui';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { Building2, CreditCard, type LucideIcon, ScrollText, Settings, ShieldAlert } from 'lucide-react';
import { SideGroup, SideLink, StageShell, UserMenu } from '../components/stage-shell.js';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';

const NAV: { label: string; Icon: LucideIcon; to: '/clinicas' | '/ajustes' }[] = [
  { label: 'Clínicas', Icon: Building2, to: '/clinicas' },
  { label: 'Configurações', Icon: Settings, to: '/ajustes' },
];
const NEXT: { label: string; Icon: LucideIcon }[] = [
  { label: 'Auditoria', Icon: ScrollText },
  { label: 'Planos e cobrança', Icon: CreditCard },
];

/** Casca do console: mesmo desenho do painel, com o selo do ambiente para ninguém confundir. */
export function PlatformShell() {
  const { data: me } = useSuspenseQuery(meQuery);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const current = path.startsWith('/perfil') ? { label: 'Meu perfil' } : NAV.find((i) => path.startsWith(i.to));
  const logout = useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST' }),
    onSettled: () => { qc.clear(); void navigate({ to: '/entrar' }); },
  });

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="px-1"><BrandMark /></div>
      <div className="mt-6 flex items-center gap-2.5 rounded-2xl border border-warning/35 bg-warning-soft px-3 py-2.5">
        <ShieldAlert className="size-4 shrink-0 text-warning" aria-hidden />
        <span className="text-[10.5px] leading-tight font-semibold tracking-[0.16em] text-fg uppercase">Console da plataforma</span>
      </div>
      <nav aria-label="Console" className="mt-7 flex-1">
        <SideGroup label="Administração">
          {NAV.map((item) => <li key={item.to}><SideLink to={item.to} Icon={item.Icon} label={item.label} /></li>)}
        </SideGroup>
        <SideGroup label="Em breve">
          {NEXT.map((item) => (
            <li key={item.label} className="flex h-10 items-center gap-3.5 px-3 text-[14.5px] font-medium text-fg-subtle" aria-disabled>
              <item.Icon className="size-[19px]" aria-hidden />{item.label}
            </li>
          ))}
        </SideGroup>
      </nav>
      <p className="mt-4 px-3 text-[11.5px] leading-snug text-fg-subtle">Tudo o que você altera aqui vale para todas as clínicas.</p>
    </div>
  );

  return (
    <StageShell sidebar={sidebar} section={current && { group: 'Console', page: current.label }}
      topbar={<UserMenu name={me.user.name} subtitle="Administrador da plataforma" profileTo="/perfil" onLogout={() => logout.mutate()} />} />
  );
}

export function NoPlatformAccess() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return (
    <div className="grid min-h-dvh place-items-center px-6 text-center">
      <div className="max-w-sm">
        <span className="mx-auto grid size-12 place-items-center rounded-full bg-danger-soft text-danger"><ShieldAlert className="size-6" /></span>
        <h1 className="mt-4 text-xl font-semibold text-fg">Acesso restrito</h1>
        <p className="mt-2 text-sm text-fg-muted">Este console é exclusivo da administração da plataforma. Sua conta não tem essa permissão.</p>
        <button type="button" className="mt-6 text-sm font-medium text-primary hover:underline" onClick={async () => {
          await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
          qc.clear();
          void navigate({ to: '/entrar' });
        }}>Entrar com outra conta</button>
      </div>
    </div>
  );
}
